import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge, Button, Toast } from '../ui';
import Drawer from './Drawer';
import {
  getProductCategories,
  setProductCategories,
  listCategoryTrees,
  getCategoryTreeNodes,
} from '../../services/pimApi';
import './pim.css';

function flatten(nodes, depth = 0, out = []) {
  nodes.forEach((n) => {
    out.push({ ...n, depth });
    flatten(n.children || [], depth + 1, out);
  });
  return out;
}

/**
 * Sidebar panel for the product editor: shows the product's category
 * assignments grouped by tree, with a per-tree picker drawer.
 */
export default function ProductCategoriesPanel({ sku }) {
  const [assigned, setAssigned] = useState([]);
  const [trees, setTrees] = useState([]);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  // Picker drawer state
  const [picker, setPicker] = useState(null); // {tree}
  const [pickerNodes, setPickerNodes] = useState([]);
  const [pickerSelected, setPickerSelected] = useState(() => new Set());
  const [pickerLoading, setPickerLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const [cats, treeList] = await Promise.all([
        getProductCategories(sku),
        listCategoryTrees(),
      ]);
      setAssigned(cats.categories || []);
      setTrees((treeList.trees || []).filter((t) => t.enabled !== false));
    } catch (e) {
      // Product may not exist in the PIM yet; keep the panel quiet.
      setAssigned([]);
    }
  }, [sku]);

  useEffect(() => {
    load();
  }, [load]);

  const byTree = useMemo(() => {
    const map = {};
    assigned.forEach((c) => {
      (map[c.tree_code] = map[c.tree_code] || []).push(c);
    });
    return map;
  }, [assigned]);

  const openPicker = async (tree) => {
    setPicker({ tree });
    setPickerLoading(true);
    setFilter('');
    try {
      const data = await getCategoryTreeNodes(tree.code);
      const flat = flatten(data.nodes || []);
      setPickerNodes(flat);
      setPickerSelected(
        new Set((byTree[tree.code] || []).map((c) => c.id))
      );
    } catch (e) {
      setToast({ message: `Failed to load tree "${tree.code}"`, error: true });
      setPicker(null);
    } finally {
      setPickerLoading(false);
    }
  };

  const togglePick = (id) =>
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const savePicker = async () => {
    if (!picker || busy) return;
    setBusy(true);
    try {
      await setProductCategories(sku, Array.from(pickerSelected), picker.tree.code);
      setToast({ message: `Categories updated for ${picker.tree.label}` });
      setPicker(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Failed to save categories', error: true });
    } finally {
      setBusy(false);
    }
  };

  const visibleNodes = filter
    ? pickerNodes.filter(
        (n) =>
          n.label.toLowerCase().includes(filter.toLowerCase()) ||
          n.code.toLowerCase().includes(filter.toLowerCase())
      )
    : pickerNodes;

  return (
    <>
      <Card title="Categories">
        {trees.length === 0 && <p className="pim-muted">No category trees configured.</p>}
        {trees.map((tree) => {
          const cats = byTree[tree.code] || [];
          return (
            <div key={tree.code} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <strong style={{ fontSize: 13, flex: 1 }}>
                  <Link to={`/pim/categories/${encodeURIComponent(tree.code)}`}>{tree.label}</Link>
                </strong>
                <Button size="sm" variant="ghost" onClick={() => openPicker(tree)}>
                  Edit
                </Button>
              </div>
              {cats.length ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {cats.map((c) => (
                    <span key={c.id} title={c.code}>
                      <Badge variant={c.is_holding_area ? 'warning' : 'neutral'}>{c.label}</Badge>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="pim-muted" style={{ margin: 0, fontSize: 12 }}>
                  Not categorised
                </p>
              )}
            </div>
          );
        })}
      </Card>

      {picker && (
        <Drawer
          title={`Categories: ${picker.tree.label}`}
          sub={`Select the categories for ${sku} in this tree.`}
          onClose={() => setPicker(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPicker(null)}>Cancel</Button>
              <Button onClick={savePicker} disabled={busy || pickerLoading}>
                Save ({pickerSelected.size})
              </Button>
            </>
          }
        >
          <div className="pim-field">
            <input
              type="text"
              placeholder="Filter categories…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          {pickerLoading ? (
            <p className="pim-muted">Loading…</p>
          ) : (
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {visibleNodes.map((n) => (
                <label
                  key={n.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 4px',
                    paddingLeft: 4 + (filter ? 0 : n.depth * 18),
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={pickerSelected.has(n.id)}
                    onChange={() => togglePick(n.id)}
                  />
                  <span style={{ flex: 1 }}>
                    {n.label}
                    {n.is_holding_area && <Badge variant="warning">Holding</Badge>}
                  </span>
                  <code style={{ fontSize: 11, color: '#99a' }}>{n.code}</code>
                </label>
              ))}
              {visibleNodes.length === 0 && <p className="pim-muted">No matching categories.</p>}
            </div>
          )}
        </Drawer>
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
